import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { UserStatus, USER_ROUTES, STATUS_PRIORITY, BYPASS_REDIRECT_PATHS } from '../types';
import { Box, CircularProgress } from '@mui/material';
import { useUserStatus } from '../useUserStatus';

interface StatusRouterProps {
  children?: React.ReactNode;
  allowedStatuses?: UserStatus[];
  requireExactMatch?: boolean;
  fallbackRoute?: string;
  bypassPaths?: string[];
}

const StatusRouter: React.FC<StatusRouterProps> = ({
  children,
  allowedStatuses = [],
  requireExactMatch = false,
  fallbackRoute,
  bypassPaths = BYPASS_REDIRECT_PATHS,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, loading } = useUserStatus();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    // Don't redirect if still loading or no status
    // Note: Authentication is already checked by ProtectedRoute/StatusProtectedRoute
    if (loading || !status) {
      return;
    }

    // Don't redirect if current path is in bypass list
    if (bypassPaths.includes(location.pathname)) {
      hasNavigatedRef.current = false;
      return;
    }

    // Don't redirect if current status is allowed for this route
    if (allowedStatuses.includes(status)) {
      hasNavigatedRef.current = false;
      return;
    }

    // Prevent multiple rapid navigations
    if (hasNavigatedRef.current) {
      return;
    }

    // Determine target route based on status priority or fallback
    let targetRoute: string | null = null;

    if (requireExactMatch || allowedStatuses.length === 0) {
      // Use status priority to find the correct route
      const redirectStatus = STATUS_PRIORITY.find((s) => s === status);
      if (redirectStatus && USER_ROUTES[redirectStatus]) {
        targetRoute = USER_ROUTES[redirectStatus];
      }
    }

    // If no target route found, use fallback or status-based route
    if (!targetRoute) {
      targetRoute = fallbackRoute || (status ? USER_ROUTES[status] : null);
    }

    // Only navigate if target route exists and is different from current path
    if (targetRoute && targetRoute !== location.pathname) {
      hasNavigatedRef.current = true;
      navigate(targetRoute, {
        replace: true,
        state: { from: location.pathname },
      });
    } else if (!targetRoute) {
      // Fallback to enrollment only if no route found
      if (location.pathname !== '/enrollment') {
        hasNavigatedRef.current = true;
        navigate('/enrollment', {
          replace: true,
          state: { from: location.pathname },
        });
      }
    }
  }, [
    status,
    loading,
    allowedStatuses,
    requireExactMatch,
    navigate,
    location.pathname,
    fallbackRoute,
    bypassPaths,
  ]);

  // Reset navigation flag when pathname changes (navigation completed)
  useEffect(() => {
    hasNavigatedRef.current = false;
  }, [location.pathname]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (children) {
    if (status && (allowedStatuses.includes(status) || bypassPaths.includes(location.pathname))) {
      return <>{children}</>;
    }
    return null;
  }

  return <Outlet />;
};

export default StatusRouter;

